import React from 'react'
import { useAuthenticatedQuery, useLazyAuthenticatedQuery } from '@hooks/useAuth'
import { useParams } from 'react-router-dom'
import { Booking, BookingType } from '@customTypes/booking.interface'
import { GET_USER_BOOKINGS } from '@graphql/queries/getUserBookings.query'
import {
    Container,
    ContentContainer,
    Description,
    DescriptionTitle,
    DetailsLine,
    SectionTitle,
    Subtitle,
    Title,
    Text,
    RightContainer,
    PriceText,
    VatText,
    Line,
    DownloadInvoice
} from './BookingDetails.style'
import { useTranslation } from 'react-i18next'
import LotInfo from '@views/Checkout/components/LotInfo/LotInfo.component'
import { ActiveFeature, Lot, LotViewName } from '@customTypes/lot.interface'
import {
    BookingGetLotQuery,
    BookingGetLotQueryVariables,
    GetUserInvoiceUrlQuery,
    GetUserInvoiceUrlQueryVariables
} from 'parkdepot-shared/gql/graphql'
import { GET_LOT_WITH_ID } from '@graphql/queries/getLotWithId.query'
import { GET_USER_INVOICE_URL } from '@graphql/queries/getUserInvoiceUrl.query'
import { useQuery } from '@apollo/client'
import Badge from '@components/Badge/Badge.component'
import { DateTime } from 'luxon'
import { formatPriceWithCurrency } from '@utils/formatPriceWithCurrency.utils'
import { priceToString } from '@utils/priceToString.utils'
import { calculateVAT } from '@utils/calculateVAT.utils'
import { getDurationStatus } from '@utils/getDurationStatus.utils'
import FullScreenLoader from '@components/FullScreenLoader/FullScreenLoader.component'
import { DateFormat, DateTimeFormat } from '@constants/dateFormats.consts'
import NightParkingTimes from '@views/PaymentSuccess/components/ParkingDetails/NightParkingTimes'

const BookingDetails = () => {
    const { orderId } = useParams<{ orderId: string }>()
    const { t, i18n } = useTranslation()
    const [downloadingInvoice, setDownloadingInvoice] = React.useState(false)

    const { data: bookingsFromApi, loading } = useAuthenticatedQuery<{
        get_user_bookings: Booking[]
    }>({
        query: GET_USER_BOOKINGS,
        fetchPolicy: 'cache-first'
    })

    const bookings = bookingsFromApi?.get_user_bookings || []
    const booking = bookings.find((booking) => booking.booking_ref === orderId)
    const status = getDurationStatus(booking?.start_date || '', booking?.end_date || '')
    const duration = booking?.end_date
        ? DateTime.fromISO(booking.end_date).diff(DateTime.fromISO(booking.start_date)).as('hours')
        : null

    const { data: lotData, loading: lotDataLoading } = useQuery<BookingGetLotQuery, BookingGetLotQueryVariables>(
        GET_LOT_WITH_ID,
        {
            skip: !booking,
            variables: booking?.lot_id ? { lot_id: booking.lot_id } : undefined
        }
    )

    const [getUserInvoiceUrl] = useLazyAuthenticatedQuery<GetUserInvoiceUrlQuery, GetUserInvoiceUrlQueryVariables>({
        query: GET_USER_INVOICE_URL
    })

    const lot = lotData?.booking_get_lot
    const vatData = calculateVAT(booking?.price || 0, lot?.lot_country_code || 'DE')

    const description = lot?.description
        ? (lot.description as any)[i18n.language.split('-')[0]?.toLowerCase() || 'de']
        : ''

    const isHourlyBooking = booking?.booking_type === BookingType.HOURLY
    const isNightlyBooking = booking?.booking_type === BookingType.NIGHTLY
    const isDailyBooking = booking?.booking_type === BookingType.DAILY

    const night_parking_times = isNightlyBooking
        ? lot?.active_features?.find((feature) => feature?.name.toUpperCase() === LotViewName.NIGHTLY)?.config
              .night_parking_times
        : []

    const onDownloadInvoice = async () => {
        if (downloadingInvoice) return
        setDownloadingInvoice(true)
        try {
            const { data } = await getUserInvoiceUrl({
                variables: {
                    booking_id: booking?.booking_ref || ''
                }
            })
            if (data?.get_user_invoice_url.signed_url) {
                window.location.href = data.get_user_invoice_url.signed_url
            }
        } catch (error) {
        } finally {
            setDownloadingInvoice(false)
        }
    }

    return (
        <Container>
            {loading && <FullScreenLoader />}
            {!loading && (
                <>
                    <ContentContainer>
                        <Title>{t('checkout.success.title')}</Title>
                        <Subtitle>{t('checkout.success.details')}</Subtitle>
                        <DetailsLine>
                            <Text>{t('checkout.success.status')}</Text>
                            <Text $right>
                                <Badge variant={status.variant} text={t(status.tKey)} />
                            </Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.vehicle')}</Text>
                            <RightContainer>
                                {booking?.plates?.map((plate) => (
                                    <Text $right key={plate.plate}>
                                        {/* {vehicle.name ? `${vehicle.name}, ` : ' '} */}
                                        {plate.plate}
                                    </Text>
                                ))}
                            </RightContainer>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.email')}</Text>
                            <Text $right>{booking?.email}</Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.lenght')}</Text>
                            <Text $right>
                                {isHourlyBooking &&
                                    `${duration?.toFixed(0)} ${t(
                                        (duration || 0) > 1 ? 'common.hours' : 'common.hour'
                                    )}`}
                                {isNightlyBooking && (
                                    <NightParkingTimes
                                        active_features={lot?.active_features as ActiveFeature[]}
                                        sundayIncluded={(night_parking_times?.length ?? 0) > 1}
                                    />
                                )}
                                {isDailyBooking && `${t('checkout.allDay')}`}
                            </Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.validFrom')}</Text>
                            <Text $right>
                                {DateTime.fromISO(booking?.start_date as string).toFormat(
                                    isHourlyBooking ? DateTimeFormat : DateFormat
                                )}
                            </Text>
                        </DetailsLine>
                        {(isNightlyBooking || isDailyBooking) && (
                            <DetailsLine>
                                <Text>{t('checkout.success.validTo')}</Text>
                                <Text $right>
                                    <span>{DateTime.fromISO(booking?.end_date as string).toFormat(DateFormat)}</span>
                                </Text>
                            </DetailsLine>
                        )}
                        <DetailsLine>
                            <Text>{t('checkout.success.paymentMethod')}</Text>
                            <Text $right>{booking?.payment_method}</Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text>{t('checkout.success.bookingReference')}</Text>
                            <Text $right>{booking?.booking_ref}</Text>
                        </DetailsLine>
                        <DetailsLine>
                            <Text $bold>{t('checkout.success.price')}</Text>
                            <RightContainer>
                                <PriceText>
                                    {formatPriceWithCurrency().format(priceToString(booking?.price || 0))}
                                </PriceText>
                                {!lotDataLoading && (
                                    <VatText>
                                        {t('checkout.success.included')} {vatData.percentage}%{' '}
                                        {t('checkout.success.vat')}{' '}
                                        {formatPriceWithCurrency().format(priceToString(vatData.value))}
                                    </VatText>
                                )}
                            </RightContainer>
                        </DetailsLine>
                        <RightContainer>
                            <DownloadInvoice onClick={onDownloadInvoice} $loading={downloadingInvoice}>
                                {t('invoice.downloadInvoice')}
                            </DownloadInvoice>
                        </RightContainer>
                    </ContentContainer>
                    <Line />
                    <ContentContainer>
                        <SectionTitle>{t('checkout.success.parkingInformation')}</SectionTitle>
                        <LotInfo lot={lot as unknown as Lot} boldedTitles column />
                        <DescriptionTitle>{t('checkout.success.description')}</DescriptionTitle>
                        <Description dangerouslySetInnerHTML={{ __html: description }} />
                    </ContentContainer>
                </>
            )}
        </Container>
    )
}

export default BookingDetails
